import { useIssueForm } from 'store/issueForm';
import { useLevels } from 'store/level';
import { useSites } from 'store/site';
import { useAllCompanies } from 'store/company';
import { useContracts } from 'store/contract/hooks';
import { useProcesses } from 'store/process';
import { keepUndeleted } from 'shared/domain/deletable/filters';
import { useCreateStore, Store } from 'hooks/createStore';
import { useEffect, useMemo, useRef } from 'react';
import { HashMap } from 'shared/types/commonView';
import { generateForm, toFormFieldValues } from 'views/issue/issueView/UnifiedIssueViewContainer/generateForm';
import { LegacyIssueForm } from 'views/issue/issueView/UnifiedIssueViewContainer/types';
import { toFieldValuesOfProcess, useFieldValueDictionaries } from 'store/fieldValue';

export function useGenerateForms(): {
  forms: HashMap<LegacyIssueForm>;
  loading: boolean;
  loadingStore: Store<boolean>;
} {
  const { issueForm } = useIssueForm();
  const { processes, loading: loadingProcesses } = useProcesses();
  const {
    items: sites,
    loading: loadingSites,
  } = useSites();
  const {
    items: levels,
    loading: loadingLevels,
  } = useLevels();
  const { items: companies, loading: loadingCompanies } = useAllCompanies();
  const { items: contracts, loading: loadingContracts } = useContracts();
  const { dictionaries, loading: loadingFieldValues } = useFieldValueDictionaries();

  const loading =
    loadingProcesses ||
    loadingSites ||
    loadingLevels ||
    loadingCompanies ||
    loadingContracts ||
    loadingFieldValues;

  const loadingStore = useCreateStore(loading);

  useEffect(() => {
    loadingStore.set(loading);
  }, [loading, loadingStore]);

  const resultRef = useRef({
    forms: {},
    loading: loading,
    loadingStore,
  });

  return useMemo(() => {
    const result: Record<string, LegacyIssueForm> = {};
    if (loading) return resultRef.current;
    processes.forEach((process) => {
      if (!issueForm) {
        return;
      }
      const form = generateForm({
        issueForm: JSON.parse(JSON.stringify(issueForm)),
        ...toFormFieldValues(toFieldValuesOfProcess(dictionaries, process._id)),
        levels: levels.filter(keepUndeleted),
        sites: sites.filter(keepUndeleted),
        companies: companies.filter(keepUndeleted),
        contracts: contracts.filter(keepUndeleted),
      });
      result[process._id] = form;
    });
    return {
      forms: result,
      loading: loading,
      loadingStore,
    };
  }, [
    processes,
    issueForm,
    dictionaries,
    levels,
    sites,
    companies,
    contracts,
    loading,
    loadingStore,
  ]);
}
